import { FINISHED_STATES } from '@mode/shared/contract-common';
import {
  extractVizElements,
  getBodyElementsFromLayout,
  getChartTokensFromElements,
  getTableTokensFromElements,
} from '@mode/shared/util-js';
import { flatMap } from 'lodash';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import * as fromReportViewer from './report-viewer.reducer';

function elementsEqual(a: Element[], b: Element[]) {
  if (a && b) {
    return a === b || (a.length === b.length && a.every((e, i) => e.outerHTML === b[i].outerHTML));
  }

  return false;
}

const createElementsSelector = createSelectorCreator(defaultMemoize, elementsEqual as any);

// This is the original NgRx selector
// export const selectReportViewerState = createFeatureSelector<fromReportViewer.ReportViewerFeatureState>(
//   fromReportViewer.reportViewerFeatureKey
// );

export interface ReportViewerDomainState {
  [fromReportViewer.reportViewerFeatureKey]: fromReportViewer.ReportViewerFeatureState;
}

export const selectReportViewerState = (state: ReportViewerDomainState) =>
  state[fromReportViewer.reportViewerFeatureKey];

export const getToken = createSelector(selectReportViewerState, (reportViewer) => reportViewer.token);
export const getReportRunToken = createSelector(selectReportViewerState, (reportViewer) => reportViewer.reportRunToken);

export const getLayout = createSelector(selectReportViewerState, (reportViewer) => reportViewer.layout);
export const getTheme = createSelector(selectReportViewerState, (reportViewer) => reportViewer.theme);
export const getName = createSelector(selectReportViewerState, (reportViewer) => reportViewer.displayName);

export const getSpaceName = createSelector(selectReportViewerState, (reportViewer) => reportViewer.spaceName);
export const getSpaceUrl = createSelector(selectReportViewerState, (reportViewer) => reportViewer.spaceUrl);
export const getSpaceDefaultAccess = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.spaceDefaultAccess
);
export const getIsSpaceViewable = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.isSpaceViewable
);
export const getIsCollectionCommunity = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.isCollectionCommunity
);
export const getIsCollectionPersonal = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.isCollectionPersonal
);

export const getCreator = createSelector(selectReportViewerState, (reportViewer) => reportViewer.creator);

export const getDescription = createSelector(selectReportViewerState, (reportViewer) => reportViewer.description);
export const getAccountUsername = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.accountUsername
);
export const getFullWidth = createSelector(selectReportViewerState, (reportViewer) => reportViewer.fullWidth);
export const getHasRun = createSelector(selectReportViewerState, (reportViewer) => reportViewer.hasSuccessfulReportRun);
export const getIframeSrc = createSelector(selectReportViewerState, (reportViewer) => reportViewer.iframeSrc);
export const getWebLink = createSelector(selectReportViewerState, (reportViewer) => reportViewer.webLink);
export const getDrilldownsEnabled = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.drilldownsEnabled
);
export const getHasPublished = createSelector(selectReportViewerState, (reportViewer) => !!reportViewer.publishedAt);
export const getLastSuccessfullyRunAt = createSelector(
  selectReportViewerState,
  (reportViewer) => new Date(reportViewer.lastSuccessfullyRunAt)
);
export const getCreatedAt = createSelector(selectReportViewerState, (reportViewer) => reportViewer.createdAt);
export const getMostRecentReportRunToken = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.mostRecentReportRunToken
);
export const getMostRecentReportRunState = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.mostRecentReportRunState
);
export const getExpectedRuntime = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.expectedRuntime
);
export const getManualRunDisabled = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.manualRunDisabled
);

export const getReportRunCompletedAt = createSelector(
  selectReportViewerState,
  (reportViewer) => new Date(reportViewer.reportRunCompletedAt)
);

export const getDrillAnywhereEnabled = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.drillAnywhereEnabled
);

/**
 * TODO: reduce new report run status into state
 * https://github.com/mode/issues/issues/13051
 *
 * For now, this should be treated as the report's inital run state at page load time as
 * reportRunState$ does not update when the run status changes
 */
export const getReportRunState = createSelector(selectReportViewerState, (reportViewer) => reportViewer.reportRunState);

export const getReportHasFailuresSinceLastSuccess = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.reportHasFailuresSinceLastSuccess
);

export const getIsLatestReportRun = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.isLatestReportRun
);

export const getHasRecentReportRun = createSelector(
  selectReportViewerState,
  (reportViewer) =>
    reportViewer.mostRecentReportRunToken !== reportViewer.reportRunToken &&
    FINISHED_STATES.includes(reportViewer.mostRecentReportRunState)
);

export const getActiveToastId = createSelector(selectReportViewerState, (reportViewer) => reportViewer?.activeToastId);

export const getIsLatestSuccessfulReportRun = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.isLatestSuccessfulReportRun
);
export const getShowFilterHeader = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.showFilterHeader
);
export const getHasNotebook = createSelector(selectReportViewerState, (reportViewer) => reportViewer.hasNotebook);
export const getFormFields = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.reportRunFormFields
);
export const getAreFormFieldsDynamic = createSelector(getFormFields, (fields) => fields?.some((f) => f.is_dynamic));
export const getShowReportHistory = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.showReportHistory
);
export const getParentReportName = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.parentReportName
);
export const getParentReportLink = createSelector(
  selectReportViewerState,
  (reportViewer) => reportViewer.parentReportLink
);

export const parsedLayoutGf = createSelector(getLayout, (layout) => {
  return layout ? getBodyElementsFromLayout(layout) : [];
});

export const getBodyElementsGf = createSelector(getLayout, (layout) => {
  return layout ? getBodyElementsFromLayout(layout) : [];
});

export const vizElementsInLayoutGf = createElementsSelector(parsedLayoutGf, (bodyElements) =>
  extractVizElements(bodyElements)
);

export const chartTokensInLayoutGf = createElementsSelector(vizElementsInLayoutGf, (visualElements) =>
  getChartTokensFromElements(visualElements)
);

export const tableTokensInLayoutGf = createElementsSelector(vizElementsInLayoutGf, (visualElements) =>
  getTableTokensFromElements(visualElements)
);

export const hasScriptTagGf = createSelector(getLayout, (layout) => {
  const [body] = getBodyElementsFromLayout(`<div>${layout}</div>`);
  const elements = flatMap(body.children);
  const scriptFlag = elements.some((e) =>
    e.nodeName === 'SCRIPT' || Array.from(e.querySelectorAll('SCRIPT')).length > 0 ? true : false
  );
  return scriptFlag;
});

export const hasVizElementsInLayoutGf = createElementsSelector(
  vizElementsInLayoutGf,
  (elements) => elements.length > 0
);

export const isReportBuilderEmpty = createSelector(
  hasScriptTagGf,
  hasVizElementsInLayoutGf,
  (hasScriptTag, hasVizElementsInLayoutGf) => {
    return !(hasScriptTag || hasVizElementsInLayoutGf);
  }
);
